import {
  ColumnDef,
  OnChangeFn,
  PaginationState,
  SortingState,
  TableOptions,
  getCoreRowModel,
} from '@tanstack/react-table'
import { CrewTable } from 'components/elements/crewTable/crewTable'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { CrewBadge } from 'components/elements/crewBadge/crewBadge'
import { CrewLink } from 'components/elements/crewLink/crewLink'
import { getDefaultTabValue, getEnumDataByValue } from '@crew/utils/enum'
import {
  CrewTaskListItemName,
  DetailTaskSearchOptions,
  TaskDetailListTabs,
  TaskPriorities,
} from 'enums/app'
import { CrewTaskPriority } from 'components/elements/crewTaskPriority/crewTaskPriority'
import { CrewUserItem } from 'components/elements/crewUserItem/crewUserItem'
import { ProjectTask } from '@crew/apis/project/models/getProjectTasks/response'
import { useTranslation } from '@crew/modules/i18n'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import qs from 'qs'
import {
  getParamAsArray,
  getParamAsDate,
  getParamAsString,
  getExpiredTaskRowClassName,
} from 'utils'
import {
  DEFAULT_PAGING_PARAMS,
  TASK_DEFAULT_SORT_COLUMN,
} from 'configs/constants'
import _ from 'lodash'
import { useUserSetting } from '@crew/states'
import { SettingKeyType } from '@crew/enums/app'
import { useAppSelector } from 'states/hooks'
import { useGetProjectTasksQuery } from '@crew/apis/project/projectApis'
import { skipToken } from '@reduxjs/toolkit/query'
import { GetProjectTasksRequest } from '@crew/apis/project/models/getProjectTasks/request'

export const ProjectTaskTable = memo(() => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { projectId } = useParams()

  const taskEventMessage = useAppSelector((state) => state.app.taskEventMessage)

  const projectTaskEventMessage = useAppSelector(
    (state) => state.app.projectTaskEventMessage
  )

  const params = qs.parse(searchParams.toString())

  const defaultListDisplayNumber = useUserSetting(
    SettingKeyType.ListDisplayNumber,
    DEFAULT_PAGING_PARAMS.pageSize
  )

  const pagination: PaginationState = useMemo(
    () => ({
      pageIndex: Number(
        getParamAsString('pageIndex', params) ?? DEFAULT_PAGING_PARAMS.pageIndex
      ),
      pageSize: Number(
        getParamAsString('pageSize', params) ?? defaultListDisplayNumber
      ),
    }),
    [defaultListDisplayNumber, params]
  )

  const sorting: SortingState = useMemo(() => {
    const sortArray =
      getParamAsArray('sort', params) || TASK_DEFAULT_SORT_COLUMN
    return sortArray.map((sort) => {
      const [id, direction] = sort.split('.')
      return {
        id,
        desc: direction === 'desc',
      }
    })
  }, [params])

  const [columnVisibility, setColumnVisibility] = useState({})
  const [columnPinning] = useState({
    left: ['select'],
    right: ['action'],
  })

  // ソート順。指定されていない場合はIDの降順
  const sortParams = useMemo(() => {
    const sortParams = searchParams.getAll('sort')
    return sortParams.length > 0 ? sortParams : TASK_DEFAULT_SORT_COLUMN
  }, [searchParams])

  const getProjectTasksParams: GetProjectTasksRequest | undefined = projectId
    ? {
        keyword: getParamAsString(DetailTaskSearchOptions.Keyword.id, params),
        taskKindIds: getParamAsArray(
          DetailTaskSearchOptions.TaskKindId.id,
          params
        ),
        assignToUser: getParamAsString(
          DetailTaskSearchOptions.AssignToUser.id,
          params
        ),
        taskStateIds: getParamAsArray(
          DetailTaskSearchOptions.TaskStateId.id,
          params
        ),
        taskStateTypes: getParamAsArray(
          DetailTaskSearchOptions.TaskStateType.id,
          params
        ),
        taskPriorities: getParamAsArray(
          DetailTaskSearchOptions.TaskPriority.id,
          params
        )?.map((taskPriority) => Number(taskPriority)), // string[] -> number[]
        taskCategoryIds: getParamAsArray(
          DetailTaskSearchOptions.TaskCategoryId.id,
          params
        ),
        projectId,
        startDate: getParamAsDate(DetailTaskSearchOptions.StartDate.id, params),
        dueDate: getParamAsDate(DetailTaskSearchOptions.DueDate.id, params),
        createdById: getParamAsString(
          DetailTaskSearchOptions.CreatedById.id,
          params
        ),
        updatedById: getParamAsString(
          DetailTaskSearchOptions.UpdatedById.id,
          params
        ),
        createdAt: getParamAsDate(DetailTaskSearchOptions.CreatedAt.id, params),
        updatedAt: getParamAsDate(DetailTaskSearchOptions.UpdatedAt.id, params),
        targetDateFrom: undefined,
        targetDateTo: undefined,
        limit: pagination.pageSize,
        offset: pagination.pageIndex * pagination.pageSize,
        sort: sortParams,
      }
    : undefined

  const { data: getProjectTasksResult, refetch: getProjectTasksRefetch } =
    useGetProjectTasksQuery(getProjectTasksParams ?? skipToken)

  const pageCount = Math.ceil(
    (getProjectTasksResult?.totalCount ?? 0) / pagination.pageSize
  )

  // refresh task list
  useEffect(() => {
    getProjectTasksRefetch()
  }, [getProjectTasksRefetch, taskEventMessage, projectTaskEventMessage])

  // https://github.com/TanStack/table/discussions/3899
  // https://github.com/TanStack/table/discussions/3619
  // https://github.com/infonomic/remix.infonomic.io/blob/d3a7f628d3ad6e1e80cc80d4ac72db74da90e8d6/app/routes/admin%2B/users.tsx#L116
  // Func handle change pagination
  const handlePaginationChange: OnChangeFn<PaginationState> = useCallback(
    (updaterOrValue) => {
      let values: PaginationState
      if (updaterOrValue instanceof Function) {
        values = updaterOrValue(pagination)
      } else {
        values = updaterOrValue
      }

      const newParams = {
        ...params,
        pageIndex: values.pageIndex,
        pageSize: values.pageSize,
      }

      // paramsが変わっていない場合はnavigateしない
      if (_.isEqual(params, newParams)) return

      const newQueryString = qs.stringify(newParams, {
        arrayFormat: 'repeat',
        skipNulls: true,
      })
      navigate(`?${newQueryString}`)
    },
    [navigate, pagination, params]
  )

  // Func handle change sorting
  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updaterOrValue) => {
      let values: SortingState
      if (updaterOrValue instanceof Function) {
        values = updaterOrValue(sorting)
      } else {
        values = updaterOrValue
      }

      const sortList = values.map((sort) => {
        return `${sort.id}.${sort.desc ? 'desc' : 'asc'}`
      })

      const newParams = {
        ...params,
        sort: sortList,
      }

      // paramsが変わっていない場合はnavigateしない
      if (_.isEqual(params, newParams)) return

      const newQueryString = qs.stringify(newParams, {
        arrayFormat: 'repeat',
        skipNulls: true,
      })
      navigate(`?${newQueryString}`)
    },
    [navigate, params, sorting]
  )

  const columns = useMemo<ColumnDef<ProjectTask>[]>(
    () => [
      {
        id: CrewTaskListItemName.ReferenceNo,
        accessorKey: CrewTaskListItemName.ReferenceNo,
        header: () => t('label.referenceNo'),
        cell: ({ row }) => (
          <div className="text-right w-full truncate">
            {row.original.referenceNo}
          </div>
        ),
        size: 80,
        minSize: 50,
      },
      {
        id: CrewTaskListItemName.TaskKind,
        accessorKey: CrewTaskListItemName.TaskKind,
        header: () => t('label.classification'),
        cell: ({ row }) => (
          <CrewBadge displayColor={row.original.taskKind.displayColor}>
            {row.original.taskKind.name}
          </CrewBadge>
        ),
        size: 120,
        minSize: 90,
      },
      {
        id: CrewTaskListItemName.Subject,
        accessorKey: CrewTaskListItemName.Subject,
        header: () => t('label.subject'),
        cell: ({ row }) => (
          <div className="w-full">
            <CrewLink
              to={`/tasks/${row.original.id}/${getDefaultTabValue(
                TaskDetailListTabs
              )}`}
              title={row.original.subject}
              className="line-clamp-2 break-all"
            >
              {row.original.subject}
            </CrewLink>
          </div>
        ),
        size: 500,
        minSize: 50,
      },
      {
        id: CrewTaskListItemName.TaskPriority,
        accessorKey: CrewTaskListItemName.TaskPriority,
        header: () => t('label.priority'),
        cell: ({ row }) => {
          const taskPriority = getEnumDataByValue(
            TaskPriorities,
            row.original.taskPriority
          )

          return (
            <div className="w-full">
              {taskPriority && (
                <CrewTaskPriority
                  taskPriority={taskPriority.value}
                  className="justify-center"
                />
              )}
            </div>
          )
        },
        size: 80,
        minSize: 50,
      },
      {
        id: CrewTaskListItemName.DueDate,
        accessorKey: CrewTaskListItemName.DueDate,
        header: () => t('label.dueDate'),
        cell: ({ row }) => (
          <div className="truncate">
            {t('format.date', {
              value: row.original.dueDate,
            })}
          </div>
        ),
        size: 120,
        minSize: 50,
      },
      {
        id: CrewTaskListItemName.TaskState,
        accessorKey: CrewTaskListItemName.TaskState,
        header: () => t('label.state'),
        cell: ({ row }) => (
          <CrewBadge displayColor={row.original.taskState.displayColor}>
            {row.original.taskState.name}
          </CrewBadge>
        ),
        size: 120,
        minSize: 90,
      },
      {
        id: CrewTaskListItemName.AssignToUser,
        accessorKey: CrewTaskListItemName.AssignToUser,
        header: () => t('label.assignedUser'),
        cell: ({ row }) => (
          <div className="w-full">
            {row.original.assignToUser && (
              <CrewUserItem
                id={row.original.assignToUser.id}
                displayName={row.original.assignToUser.displayName}
                version={row.original.assignToUser.version}
              />
            )}
          </div>
        ),
        size: 200,
        minSize: 50,
      },
      {
        id: CrewTaskListItemName.StartDate,
        accessorKey: CrewTaskListItemName.StartDate,
        header: () => t('label.startDate'),
        cell: ({ row }) => (
          <div className="truncate">
            {t('format.date', {
              value: row.original.startDate,
            })}
          </div>
        ),
        size: 120,
        minSize: 50,
      },
      {
        id: CrewTaskListItemName.TaskCategory,
        accessorKey: CrewTaskListItemName.TaskCategory,
        header: () => t('label.category'),
        cell: ({ row }) => (
          <div className="truncate">{row.original.taskCategory?.name}</div>
        ),
        size: 160,
        minSize: 50,
      },
    ],
    [t]
  )

  const tableOptions: TableOptions<ProjectTask> = {
    data: getProjectTasksResult?.tasks ?? [],
    columns,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    pageCount,
    state: {
      pagination,
      sorting,
      columnVisibility,
      columnPinning,
    },
    onPaginationChange: handlePaginationChange,
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualPagination: true,
    manualSorting: true,
    enableMultiSort: true,
    enableRowSelection: true,
    maxMultiSortColCount: 2,
    meta: {
      headerRowHeight: 35,
      dataRowHeight: 40,
      // 行のカスタムクラスを返す関数
      // 期日が過ぎている行を赤く表示する
      getRowClassName: getExpiredTaskRowClassName<ProjectTask>,
    },
  }

  return (
    <>
      {/* project task list table */}
      <CrewTable tableOptions={tableOptions} />
    </>
  )
})
